// Suplementos por zona (zone charges) · motor de PREVISUALIZACIÓN.
//
// Port JS del cálculo del API (el SERVIDOR decide el precio al comandar;
// esto solo pinta en el TPV lo que el servidor va a cobrar). Semántica:
//   • Se aplica DESPUÉS de la promoción horaria: el porcentaje se calcula
//     sobre el precio ya rebajado.
//   • El suplemento va DENTRO del precio de cada línea (decisión del
//     dueño); el cobro/ticket lo desglosa como «Incluye Terraza: X €»
//     = Σ zoneChargeUnit × unidades.
//   • Modos:
//       PERCENT_LINE      → unidad = redondeo(precio × % / 100)
//       AMOUNT_PER_UNIT   → unidad = importe fijo por unidad (no al peso)
//       FIXED_PER_TICKET  → NO va en las líneas: el servidor añade una
//                           línea de sistema al ticket. Aquí se ignora.
//   • Productos al peso: solo PERCENT_LINE (sobre el importe pesado).
//   • Categorías excluidas: la del producto, su subcategoría o su padre.
//   • Días/franja/fechas con la misma semántica que las promociones
//     (franja que cruza medianoche = día de inicio, fin exclusivo).
//   • Canales: bitmask TPV = 1, QR = 2. Sin campo → todos.
//   • Líneas «Otros»/precio libre o editadas a mano no se tocan.
//   • Si varios suplementos casan con la misma zona y línea, se aplica
//     UNO: el de mayor importe; empate → menor id (estable).
import {
  PRICE_RULE_CHANNELS,
  evaluatePrice,
  isLineExcluded,
  productCategoryIds,
  roundCents,
  ruleWindowStartDate,
  toMoment,
} from "./pricingEngine";

export const ZONE_CHARGE_MODES = Object.freeze({
  PERCENT_LINE: "PERCENT_LINE",
  AMOUNT_PER_UNIT: "AMOUNT_PER_UNIT",
  FIXED_PER_TICKET: "FIXED_PER_TICKET",
});

const ALL_CHANNELS = PRICE_RULE_CHANNELS.TPV | PRICE_RULE_CHANNELS.QR;
const asArray = (v) => (Array.isArray(v) ? v : []);

/** Normaliza un suplemento del API (camelCase o PascalCase). */
export function normalizeZoneCharge(z) {
  if (!z || typeof z !== "object") return null;
  const pick = (...keys) => {
    for (const k of keys) if (z[k] !== undefined) return z[k];
    return undefined;
  };
  const weekdays = pick("weekdays", "Weekdays");
  const channels = pick("channels", "Channels");
  const st = pick("startTime", "StartTime");
  const et = pick("endTime", "EndTime");
  const vf = pick("validFrom", "ValidFrom");
  const vt = pick("validTo", "ValidTo");
  const zonaId = pick("zonaId", "ZonaId", "zoneId", "ZoneId");
  return {
    id: pick("id", "Id"),
    zonaId: zonaId == null ? null : Number(zonaId),
    name: pick("name", "Name") || "",
    mode: String(pick("mode", "Mode") || ZONE_CHARGE_MODES.PERCENT_LINE).toUpperCase(),
    value: Number(pick("value", "Value")) || 0,
    weekdays: weekdays == null ? 127 : Number(weekdays) || 0,
    startTime: st ? String(st).slice(0, 5) : null,
    endTime: et ? String(et).slice(0, 5) : null,
    validFrom: vf ? String(vf).slice(0, 10) : null,
    validTo: vt ? String(vt).slice(0, 10) : null,
    channels: channels == null ? ALL_CHANNELS : Number(channels) || 0,
    isActive: pick("isActive", "IsActive") !== false,
    excludedCategoryIds: asArray(pick("excludedCategoryIds", "ExcludedCategoryIds")).map(Number),
  };
}

function channelMatches(charge, channel) {
  const c = charge?.channels == null ? ALL_CHANNELS : Number(charge.channels);
  return ((Number.isFinite(c) ? c : ALL_CHANNELS) & channel) !== 0;
}

/** Importe del suplemento por unidad (o null si no aplica). */
export function computeZoneChargeUnit(charge, price, { isWeight = false } = {}) {
  const p = Number(price);
  const v = Number(charge?.value);
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(v) || v <= 0) return null;
  const mode = String(charge?.mode || "").toUpperCase();
  let unit;
  if (mode === ZONE_CHARGE_MODES.PERCENT_LINE) unit = roundCents((p * v) / 100);
  else if (mode === ZONE_CHARGE_MODES.AMOUNT_PER_UNIT) {
    if (isWeight) return null;
    unit = roundCents(v);
  } else return null; // FIXED_PER_TICKET u otro → línea de sistema del servidor
  return unit > 0 ? unit : null;
}

/**
 * Suplemento de zona para una línea.
 *
 * @param {object} args
 * @param {object} args.product
 * @param {number} args.price        precio de la línea DESPUÉS de la promoción
 * @param {object} [args.line]
 * @param {Array}  args.zoneCharges
 * @param {*}      args.at           momento (ver `toMoment`)
 * @param {number|null} args.zonaId  zona de la mesa
 * @param {number[]} [args.categoryIds]
 * @param {number} [args.channel]
 * @returns {null | {precio, zoneChargeId, zoneChargeName, zoneChargeUnit}}
 */
export function evaluateZoneCharge({
  product,
  price,
  line = null,
  zoneCharges,
  at,
  zonaId = null,
  categoryIds = null,
  channel = PRICE_RULE_CHANNELS.TPV,
}) {
  if (!product || zonaId == null || !Array.isArray(zoneCharges) || zoneCharges.length === 0) return null;
  if (isLineExcluded(product, line)) return null;
  const moment = toMoment(at);
  if (!moment) return null;
  const isWeight = !!product.ventaAlPeso;
  const cats = Array.isArray(categoryIds) ? categoryIds.map(Number) : productCategoryIds(product);

  let best = null;
  for (const charge of zoneCharges) {
    if (!charge || charge.isActive === false) continue;
    const cz = charge.zonaId ?? charge.zoneId;
    if (cz == null || Number(cz) !== Number(zonaId)) continue;
    if (!channelMatches(charge, channel)) continue;
    const excluded = asArray(charge.excludedCategoryIds).map(Number);
    if (excluded.length > 0 && cats.some((c) => excluded.includes(c))) continue;
    if (ruleWindowStartDate(charge, moment) == null) continue;
    const unit = computeZoneChargeUnit(charge, price, { isWeight });
    if (unit == null) continue;
    if (!best || unit > best.unit || (unit === best.unit && Number(charge.id) < Number(best.charge.id))) {
      best = { charge, unit };
    }
  }
  if (!best) return null;
  return {
    precio: roundCents(Number(price) + best.unit),
    zoneChargeId: best.charge.id ?? null,
    zoneChargeName: best.charge.name || "Suplemento",
    zoneChargeUnit: best.unit,
  };
}

/**
 * Precio de una línea: promoción horaria (si hay) + suplemento de zona
 * (si hay). Devuelve null si nada cambia el precio de carta.
 */
export function evaluateLinePrice(args) {
  const { product, basePrice, rules, zoneCharges } = args || {};
  if (!product) return null;
  const base = basePrice != null ? Number(basePrice) : Number(product.precio);
  if (!Number.isFinite(base) || base <= 0) return null;
  const rule = Array.isArray(rules) && rules.length > 0 ? evaluatePrice(args) : null;
  const afterRule = rule ? rule.precio : base;
  const zone = Array.isArray(zoneCharges) && zoneCharges.length > 0
    ? evaluateZoneCharge({ ...args, price: afterRule })
    : null;
  if (!rule && !zone) return null;
  const out = {
    precio: zone ? zone.precio : rule.precio,
    precioCarta: roundCents(base),
    priceRuleId: rule ? rule.priceRuleId : null,
    priceRuleName: rule ? rule.priceRuleName : null,
  };
  // Campos de zona solo si hay suplemento (sin él, igual que en P1).
  if (zone) {
    out.zoneChargeId = zone.zoneChargeId;
    out.zoneChargeName = zone.zoneChargeName;
    out.zoneChargeUnit = zone.zoneChargeUnit;
  }
  return out;
}

/**
 * Suplemento FIXED_PER_TICKET vigente para la zona (el servidor añade la
 * línea de sistema; aquí solo para previsualizar/paridad). El mayor
 * importe gana; empate → menor id.
 */
export function evaluateTicketCharge({ zoneCharges, at, zonaId = null, channel = PRICE_RULE_CHANNELS.TPV }) {
  if (zonaId == null || !Array.isArray(zoneCharges)) return null;
  const moment = toMoment(at);
  if (!moment) return null;
  let best = null;
  for (const charge of zoneCharges) {
    if (!charge || charge.isActive === false) continue;
    if (String(charge.mode || "").toUpperCase() !== ZONE_CHARGE_MODES.FIXED_PER_TICKET) continue;
    const cz = charge.zonaId ?? charge.zoneId;
    if (cz == null || Number(cz) !== Number(zonaId)) continue;
    if (!channelMatches(charge, channel)) continue;
    if (ruleWindowStartDate(charge, moment) == null) continue;
    const amount = roundCents(Number(charge.value) || 0);
    if (amount <= 0) continue;
    if (!best || amount > best.amount || (amount === best.amount && Number(charge.id) < Number(best.charge.id))) {
      best = { charge, amount };
    }
  }
  return best ? { zoneChargeId: best.charge.id, name: best.charge.name || "Suplemento", amount: best.amount } : null;
}

// ── Desglose «Incluye Terraza: X €» ──────────────────────────────────────

const lineZoneName = (l) => l?.zoneChargeName ?? l?.ZoneChargeName ?? null;
const lineZoneUnit = (l) => Number(l?.zoneChargeUnit ?? l?.ZoneChargeUnit) || 0;

export const defaultLineUnits = (l) =>
  Number(l?.remaining ?? l?.quantity ?? l?.unidades ?? l?.Unidades) || 0;

/**
 * Σ zoneChargeUnit × unidades, agrupado por nombre del suplemento.
 * @returns {Array<{name: string, amount: number}>} (sin importes ≤ 0)
 */
export function zoneChargeBreakdown(lines, unitsOf = defaultLineUnits) {
  const map = new Map();
  asArray(lines).forEach((l) => {
    const name = lineZoneName(l);
    const unit = lineZoneUnit(l);
    if (!name || !(unit > 0)) return;
    const units = Number(unitsOf(l)) || 0;
    if (units <= 0) return;
    map.set(name, (map.get(name) || 0) + unit * units);
  });
  return Array.from(map.entries())
    .map(([name, amount]) => ({ name, amount: roundCents(amount) }))
    .filter((x) => x.amount > 0);
}

/** «+terraza» para la tile del TPV. */
export function zoneChargeHint(name) {
  const s = String(name || "").trim();
  return s ? `+${s.toLowerCase()}` : "";
}

export function formatZoneChargeValue(charge) {
  const v = Number(charge?.value);
  const n = Number.isFinite(v) ? v : 0;
  const money = n.toFixed(2).replace(".", ",");
  switch (String(charge?.mode || "").toUpperCase()) {
    case ZONE_CHARGE_MODES.PERCENT_LINE:
      return `+${String(n).replace(".", ",")} % por línea`;
    case ZONE_CHARGE_MODES.AMOUNT_PER_UNIT:
      return `+${money} € por unidad`;
    case ZONE_CHARGE_MODES.FIXED_PER_TICKET:
      return `+${money} € por ticket`;
    default:
      return "—";
  }
}
